import { Link, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

import PublicFiliereCohortesSessionCard from './PublicFiliereCohortesSessionCard';
import PublicFilierePlanningTrustSection from './PublicFilierePlanningTrustSection';
import PublicFiliereProgrammeSection from './PublicFiliereProgrammeSection';
import PublicFiliereTarifsBusinessCard from './PublicFiliereTarifsBusinessCard';
import PublicReveal from './PublicReveal';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import type { PublicTarifCategoryDisplay } from '../../utils/academiaTarifs';
import type { FiliereAvailability } from '../../utils/filiereInscriptionAvailability';
import { goToFormationSubscribe } from '../../utils/formationSubscribeNavigation';
import { resolveFiliereCoverImage } from '../../utils/trackCoverImage';
import {
  hasFiliereContenuPedagogique,
  parseFiliereContenuPedagogique,
} from '../../utils/filiereContenuPedagogique';
import type { PublicFiliere } from '../../utils/publicFilieres';
import {
  filterCohortesByFiliereId,
  filterPlanningByCohorteId,
  pickReferenceCohorte,
  summarizePlanningDays,
  type PublicCohorteRow,
  type PublicPlanningSession,
} from '../../utils/publicPlanningDisplay';
import '../../styles/publicFiliereDetailPage.css';

type Props = {
  filiere: PublicFiliere;
  trackSlug: string;
  domainLabel: string;
  domainIcon: string;
  /** Image de couverture du domaine (admin), fallback si la formation n’a pas d’image. */
  domaineUrlImage?: string | null;
  availability: FiliereAvailability;
  cohortes: Array<PublicCohorteRow & { idFiliere?: string }>;
  planningSessions: PublicPlanningSession[];
  planningLoading?: boolean;
  pricingCategories: PublicTarifCategoryDisplay[];
  pricingLoading?: boolean;
};

function filiereDescription(filiere: PublicFiliere): string | null {
  const raw = filiere.description?.trim();
  return raw || null;
}

function cohorteDurationLabel(cohortes: PublicCohorteRow[]): string | null {
  const withDates = cohortes.find((c) => c.dateDebut && c.dateFin);
  if (!withDates?.dateDebut || !withDates.dateFin) return null;
  const start = new Date(withDates.dateDebut);
  const end = new Date(withDates.dateFin);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const days = Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const months = Math.max(1, Math.round(days / 30));
  return `${String(months).padStart(2, '0')}`;
}

function inscritsTotal(cohortes: PublicCohorteRow[]): number {
  return cohortes.reduce((sum, c) => sum + (c.capacity?.inscrits ?? 0), 0);
}

function lowestInscriptionPrice(categories: PublicTarifCategoryDisplay[]): string | null {
  const amounts = categories
    .map((row) => row.inscription)
    .filter((value) => value && value !== '—');
  return amounts[0] ?? null;
}

export default function PublicFiliereDetailView({
  filiere,
  trackSlug,
  domainLabel,
  domainIcon: _domainIcon,
  domaineUrlImage,
  availability,
  cohortes,
  planningSessions,
  planningLoading,
  pricingCategories,
  pricingLoading,
}: Props) {
  const navigate = useNavigate();

  const inscriptionsOuvertes = availability.status === 'open';
  const filiereCohortes = useMemo(
    () => filterCohortesByFiliereId(filiere.id, cohortes),
    [filiere.id, cohortes],
  );
  const referenceCohorte = useMemo(
    () => pickReferenceCohorte(filiereCohortes),
    [filiereCohortes],
  );
  const referenceSessions = useMemo(() => {
    if (!referenceCohorte) return [];
    return filterPlanningByCohorteId(planningSessions, referenceCohorte.id);
  }, [planningSessions, referenceCohorte]);
  const seancesParSemaine = referenceSessions.length;
  const joursPlanning = summarizePlanningDays(
    referenceSessions.length > 0 ? referenceSessions : planningSessions,
  );
  const heroImage = resolveFiliereCoverImage(filiere, {
    domaineUrlImage,
    trackSlug,
  });
  const description = filiereDescription(filiere);
  const curriculum = parseFiliereContenuPedagogique(filiere.contenuPedagogique);
  const showCurriculum = hasFiliereContenuPedagogique(filiere.contenuPedagogique);
  const durationMonths = cohorteDurationLabel(filiereCohortes);
  const inscrits = inscritsTotal(filiereCohortes);
  const priceFrom = lowestInscriptionPrice(pricingCategories);

  const heroLead =
    description ??
    (joursPlanning
      ? `${filiere.libelle} — séances le ${joursPlanning.toLowerCase()}.`
      : `${filiere.libelle} — formation du domaine ${domainLabel}.`);

  const onSubscribe = () => goToFormationSubscribe(navigate, filiere);

  return (
    <div className="public-filiere-detail">
      <section className="public-filiere-detail-hero">
        <div
          className="public-filiere-detail-hero__media"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div className="public-filiere-detail-hero__veil" aria-hidden />

        <div className="public-filiere-detail-hero__inner">
          <nav className="public-filiere-detail-breadcrumb" aria-label="Fil d'Ariane">
            <Link to="/">Accueil</Link>
            <span>/</span>
            <Link to="/#filieres">Domaines</Link>
            <span>/</span>
            <Link to={`/filieres/${trackSlug}`}>{domainLabel}</Link>
            <span>/</span>
            <span>{filiere.libelle}</span>
          </nav>

          <div className="public-filiere-detail-hero__layout">
            <PublicReveal animation="fade-in-left" className="public-filiere-detail-hero__copy">
              <span className="public-filiere-detail-kicker">Formation · {filiere.code}</span>
              <h1>{filiere.libelle}</h1>
              <p>{heroLead}</p>
              <div className="public-filiere-detail-hero__actions">
                <a href="#details-formation" className="public-filiere-detail-btn-ghost">
                  <LucideSymbolIcon name="play_circle" tone="inherit" size={20} className="text-current" />
                  Voir les détails
                </a>
                {showCurriculum ? (
                  <a href="#contenu-formation" className="public-filiere-detail-btn-ghost">
                    <LucideSymbolIcon name="menu_book" tone="inherit" size={20} className="text-current" />
                    Contenu
                  </a>
                ) : null}
              </div>
            </PublicReveal>

            <PublicReveal animation="fade-in-right" delay={120} className="public-filiere-detail-hero__panel">
              <aside className="public-filiere-detail-panel" aria-label={`Résumé ${filiere.libelle}`}>
                <p className="public-filiere-detail-panel__eyebrow">En résumé</p>

                {priceFrom ? (
                  <p className="public-filiere-detail-panel__price">Inscription {priceFrom}</p>
                ) : pricingLoading ? (
                  <p className="public-filiere-detail-panel__price">Inscription …</p>
                ) : (
                  <p className="public-filiere-detail-panel__price public-filiere-detail-panel__price--muted">
                    Tarifs ci-dessous
                  </p>
                )}

                <div className="public-filiere-detail-panel__grid">
                  <div>
                    <p className="public-filiere-detail-panel__grid-label">Séances</p>
                    <p className="public-filiere-detail-panel__grid-value">
                      {planningLoading
                        ? '…'
                        : seancesParSemaine > 0
                          ? `${seancesParSemaine} / sem.`
                          : '—'}
                    </p>
                    <p className="public-filiere-detail-panel__grid-hint">par cohorte</p>
                  </div>
                  <div>
                    <p className="public-filiere-detail-panel__grid-label">Cohortes</p>
                    <p className="public-filiere-detail-panel__grid-value">
                      {filiereCohortes.length > 0 ? filiereCohortes.length : '—'}
                    </p>
                    <p className="public-filiere-detail-panel__grid-hint">ouvertes</p>
                  </div>
                </div>

                <p
                  className={`public-filiere-detail-panel__tip ${
                    availability.status === 'open'
                      ? 'public-filiere-detail-panel__tip--ok'
                      : 'public-filiere-detail-panel__tip--warn'
                  }`}
                >
                  {availability.label}
                </p>

                <div className="public-filiere-detail-panel__actions">
                  <a
                    href="#tarifs"
                    className="public-filiere-detail-panel__icon-btn"
                    aria-label="Voir les tarifs d’inscription"
                  >
                    <LucideSymbolIcon name="payments" tone="inherit" size={18} className="text-current" />
                  </a>
                  {inscriptionsOuvertes ? (
                    <button type="button" className="public-filiere-detail-panel__cta" onClick={onSubscribe}>
                      Souscrire
                      <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="text-current" />
                    </button>
                  ) : (
                    <span className="public-filiere-detail-panel__cta-disabled">Indisponible</span>
                  )}
                </div>
              </aside>
            </PublicReveal>
          </div>
        </div>
      </section>

      {showCurriculum && curriculum ? (
        <PublicFiliereProgrammeSection contenu={curriculum} />
      ) : null}

      <section className="public-filiere-detail-stats">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-8 px-4 sm:px-6 lg:grid-cols-4">
          <div>
            <p className="public-filiere-detail-stats__value">
              {filiereCohortes.length > 0 ? filiereCohortes.length : '—'}
            </p>
            <p className="public-filiere-detail-stats__label">Cohortes ouvertes</p>
          </div>
          <div>
            <p className="public-filiere-detail-stats__value">
              {planningLoading ? '…' : seancesParSemaine > 0 ? seancesParSemaine : '—'}
            </p>
            <p className="public-filiere-detail-stats__label">Créneaux / semaine</p>
          </div>
          <div>
            <p className="public-filiere-detail-stats__value">{durationMonths ?? '—'}</p>
            <p className="public-filiere-detail-stats__label">Mois / cohorte</p>
          </div>
          <div>
            <p className="public-filiere-detail-stats__value">{inscrits > 0 ? inscrits : '—'}</p>
            <p className="public-filiere-detail-stats__label">Apprenants inscrits</p>
          </div>
        </div>
      </section>

      <section id="details-formation" className="public-filiere-detail-data scroll-mt-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:max-w-7xl">
          <div className="pfd-pricing-grid">
            <PublicReveal animation="fade-in-up" className="pfd-pricing-grid__cohortes">
              <PublicFiliereCohortesSessionCard
                formationLabel={filiere.libelle}
                coverImageUrl={heroImage}
                cohortes={filiereCohortes}
              />
            </PublicReveal>

            <PublicReveal animation="fade-in-up" delay={80} className="pfd-pricing-grid__tarifs">
              <PublicFiliereTarifsBusinessCard
                pricingCategories={pricingCategories}
                priceFrom={priceFrom}
                pricingLoading={pricingLoading}
                inscriptionsOuvertes={inscriptionsOuvertes}
                onSubscribe={onSubscribe}
              />
            </PublicReveal>
          </div>
        </div>
      </section>

      <section className="pfd-planning-trust-wrap">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:max-w-7xl">
          <PublicReveal animation="fade-in-up">
            <PublicFilierePlanningTrustSection
              sessions={planningSessions}
              loading={planningLoading}
              joursPlanning={joursPlanning}
              seancesParSemaine={seancesParSemaine}
            />
          </PublicReveal>
        </div>
      </section>
    </div>
  );
}
